// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import {
  AddIcCallOutlined,
  SupportAgentOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getProjectTickets } from '@/project/api/get-project-tickets';
import { useProjectContext } from '@/project/hooks/use-project-context';
import { CreateTicketDialog } from '@/ticket/components/create-ticket-dialog';
import { TicketTableRow } from '@/ticket/components/ticket-table-row';
import { TicketTableStaticHeader } from '@/ticket/components/ticket-table-static-header';
import {
  Box,
  Button,
  Card,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableContainer,
  Tabs,
  Typography,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_app/projects/$projectKey/tickets')({
  component: ProjectTicketsRoute,
});

function ProjectTicketsRoute() {
  const { projectKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <ProjectTicketsContent projectKey={projectKey} />
    </NotFoundErrorBoundary>
  );
}

function ProjectTicketsContent(props: { projectKey: string }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { project } = useProjectContext();

  const { data } = useSuspenseQuery({
    queryKey: ['tickets', 'project', props.projectKey],
    queryFn: () => getProjectTickets(props.projectKey),
    retry: false,
  });

  const newTicketDialog = useDisclosure();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const filteredTickets = data.filter((ticket) => {
    if (selectedTab === 0) return true; // All tickets
    if (selectedTab === 1) return ticket.status === 'OPEN'; // Open tickets
    if (selectedTab === 2)
      return ticket.status === 'CLOSED' || ticket.status === 'COMPLETED'; // Closed or Completed tickets
    return false;
  });

  return (
    <Stack>
      <CreateTicketDialog
        isOpen={newTicketDialog.isOpen}
        onClose={newTicketDialog.close}
        forProjectIds={[project]}
      />

      <HardfinTabCardWrapper
        title="Tickets"
        actions={
          <Button
            size="small"
            startIcon={<AddIcCallOutlined fontSize="small" />}
            onClick={newTicketDialog.open}
          >
            Open ticket
          </Button>
        }
      >
        <Box
          sx={{
            display: 'flex',
            paddingLeft: 1,
            flexDirection: 'row',
            '& .Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiTab-root': {
              px: 2,
            },
            '& .MuiTab-root.MuiButtonBase-root': {
              ml: 0,
            },
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="Project ticket filters"
          >
            <Tab label="All" />
            <Tab label="Open" />
            <Tab label="Closed" />
          </Tabs>
        </Box>

        {filteredTickets.length === 0 ? (
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              textAlign: 'center',
              minHeight: '200px',
            }}
          >
            <SupportAgentOutlined
              sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No tickets yet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: '400px', marginBottom: 3 }}
            >
              Tickets help you manage support requests for this project
            </Typography>
          </Card>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TicketTableStaticHeader />
              <TableBody>
                {filteredTickets.map((ticket, index) => (
                  <TicketTableRow
                    key={ticket.key}
                    ticket={ticket}
                    isLastRow={index === filteredTickets.length - 1}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </HardfinTabCardWrapper>
    </Stack>
  );
}
